import ApiClient from './ApiClient';

// Surfaces backend structured errors ({ error: "..." }) instead of generic wrapping — the tasks
// screens must render the API's actual validation text (mirrors ColoClient).
const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

// The tasks DTOs serialize as the exact C# record property names (PascalCase). Wrapper keys
// (tasks/task) are already camelCase (anonymous-object responses). Normalize the DTO items to
// camelCase at the client boundary, same as ColoClient.
const toCamelKey = key => key.charAt(0).toLowerCase() + key.slice(1);

const toCamelCaseShallow = obj => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [toCamelKey(key), value])
  );
};

const toCamelCaseArray = arr => (arr ?? []).map(toCamelCaseShallow);

class TasksClient extends ApiClient {
  constructor(url) {
    super(url ? `${url}/tasks` : '/tasks');
  }

  async getCustomerTasks() {
    try {
      const data = await this.get('/customer-tasks');
      return toCamelCaseArray(data?.tasks);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to fetch tasks.'));
    }
  }

  // body: { userId, subject, description?, priority }
  async createCustomerTask(body) {
    try {
      const data = await this.post('/customer-tasks', body);
      return toCamelCaseShallow(data?.task);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to create task.'));
    }
  }

  // body: any subset of { subject, description, priority, status } — omitted fields unchanged.
  async updateCustomerTask(id, body) {
    try {
      const data = await this.patch(`/customer-tasks/${id}`, body);
      return toCamelCaseShallow(data?.task);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to update task.'));
    }
  }

  async deleteCustomerTask(id) {
    try {
      await this.delete(`/customer-tasks/${id}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to delete task.'));
    }
  }

  // ---- Miner (device) tasks ----

  async getMinerTasks() {
    try {
      const data = await this.get('/miner-tasks');
      return toCamelCaseArray(data?.tasks);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch miner tasks.')
      );
    }
  }

  // body: { deviceId, issue, assignee?, priority }
  async createMinerTask(body) {
    try {
      const data = await this.post('/miner-tasks', body);
      return toCamelCaseShallow(data?.task);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to create miner task.')
      );
    }
  }

  // body: any subset of { issue, assignee, priority, status } — omitted fields unchanged.
  async updateMinerTask(id, body) {
    try {
      const data = await this.patch(`/miner-tasks/${id}`, body);
      return toCamelCaseShallow(data?.task);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update miner task.')
      );
    }
  }

  async deleteMinerTask(id) {
    try {
      await this.delete(`/miner-tasks/${id}`);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to delete miner task.')
      );
    }
  }
}

export default TasksClient;
